import React, { useEffect, useMemo, useState } from 'react';
import { Download, RefreshCcw, Users, MessageSquare, Search } from 'lucide-react';
import { Layout } from './Home';
import { fetchSubscribers, fetchContacts } from '../services/api';

const downloadCSV = (rows, filename) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    const s = String(v ?? '').replace(/"/g, '""');
    return /[,"\n]/.test(s) ? `"${s}"` : s;
  };
  const csv = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const formatDate = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? raw : d.toLocaleString();
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl p-6 border border-[#0f5c3d]/10 flex items-center gap-4">
    <div className="w-12 h-12 rounded-xl bg-[#e2ede6] text-[#0f5c3d] flex items-center justify-center">
      <Icon className="w-6 h-6" strokeWidth={1.5} />
    </div>
    <div>
      <div
        className="text-3xl font-bold text-[#0b1a2e]"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {value}
      </div>
      <div className="text-sm text-[#0b1a2e]/70">{label}</div>
    </div>
  </div>
);

const AdminPage = ({ embedded = false }) => {
  const [tab, setTab] = useState('subscribers');
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [subRes, conRes] = await Promise.all([fetchSubscribers(), fetchContacts()]);
      setSubscribers(subRes.data || []);
      setContacts(conRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredSubs = useMemo(() => {
    const q = query.toLowerCase();
    return subscribers.filter((s) => !q || s.email.toLowerCase().includes(q));
  }, [subscribers, query]);

  const filteredContacts = useMemo(() => {
    const q = query.toLowerCase();
    return contacts.filter((c) =>
      !q ||
      c.name.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      c.subject.toLowerCase().includes(q)
    );
  }, [contacts, query]);

  const content = (
    <section className={embedded ? '' : 'bg-[#e2ede6] py-12 lg:py-16'}>
      <div className={embedded ? '' : 'max-w-7xl mx-auto px-6 lg:px-8'}>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Inbox</span>
            <h1
              className="mt-2 text-4xl lg:text-5xl text-[#0b1a2e]"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
            >
              Subscribers &amp; Messages
            </h1>
            <p className="mt-2 text-sm text-[#0b1a2e]/60">Newsletter subscribers &amp; contact form messages.</p>
          </div>
            <div className="flex items-center gap-2">
              <button
                onClick={load}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0f5c3d]/25 text-[#0b1a2e] hover:bg-white transition"
              >
                <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </button>
              <button
                onClick={() =>
                  tab === 'subscribers'
                    ? downloadCSV(subscribers, 'subscribers.csv')
                    : downloadCSV(contacts, 'contacts.csv')
                }
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white hover:bg-[#0b1a2e] transition"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <StatCard icon={Users} label="Newsletter subscribers" value={subscribers.length} />
            <StatCard icon={MessageSquare} label="Contact messages" value={contacts.length} />
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="flex gap-1 bg-white border border-[#0f5c3d]/15 rounded-full p-1">
              {['subscribers', 'contacts'].map((k) => (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  className={`px-4 py-1.5 text-sm rounded-full transition-colors ${
                    tab === k ? 'bg-[#0f5c3d] text-white' : 'text-[#0b1a2e]/70 hover:text-[#0b1a2e]'
                  }`}
                >
                  {k === 'subscribers' ? 'Subscribers' : 'Messages'}
                </button>
              ))}
            </div>
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b1a2e]/50" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="w-full bg-white border border-[#0f5c3d]/15 rounded-full pl-11 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
            {tab === 'subscribers' ? (
              <table className="w-full text-sm">
                <thead className="bg-[#e2ede6] text-[#0b1a2e]/70">
                  <tr>
                    <th className="text-left px-6 py-3 font-semibold">Email</th>
                    <th className="text-left px-6 py-3 font-semibold">Subscribed on</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubs.length === 0 ? (
                    <tr><td colSpan="2" className="px-6 py-10 text-center text-[#0b1a2e]/50">No subscribers yet.</td></tr>
                  ) : (
                    filteredSubs.map((s) => (
                      <tr key={s.id} className="border-t border-black/5 hover:bg-[#f7fbf8]">
                        <td className="px-6 py-3 text-[#0b1a2e]">{s.email}</td>
                        <td className="px-6 py-3 text-[#0b1a2e]/70">{formatDate(s.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#e2ede6] text-[#0b1a2e]/70">
                  <tr>
                    <th className="text-left px-6 py-3 font-semibold">Name</th>
                    <th className="text-left px-6 py-3 font-semibold">Email</th>
                    <th className="text-left px-6 py-3 font-semibold">Subject</th>
                    <th className="text-left px-6 py-3 font-semibold">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.length === 0 ? (
                    <tr><td colSpan="4" className="px-6 py-10 text-center text-[#0b1a2e]/50">No messages yet.</td></tr>
                  ) : (
                    filteredContacts.map((c) => (
                      <tr key={c.id} className="border-t border-black/5 hover:bg-[#f7fbf8] align-top">
                        <td className="px-6 py-3 text-[#0b1a2e] font-medium">{c.name}</td>
                        <td className="px-6 py-3 text-[#0b1a2e]/80">{c.email}</td>
                        <td className="px-6 py-3 text-[#0b1a2e]/80">
                          <div className="font-medium text-[#0b1a2e]">{c.subject}</div>
                          <div className="text-xs text-[#0b1a2e]/60 mt-1 max-w-md">{c.message}</div>
                        </td>
                        <td className="px-6 py-3 text-[#0b1a2e]/70 whitespace-nowrap">{formatDate(c.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>
  );

  return embedded ? content : <Layout>{content}</Layout>;
};

export default AdminPage;
